import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @param {{ apiBase: string; openInBrowserUrl?: string }} props
 */
export function SharePointFileBrowser({ apiBase, openInBrowserUrl }) {
  const [data, setData] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(
    (folderId = null) => {
      setLoading(true);
      setError('');
      const suffix = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '';
      return apiJson(`${apiBase}${suffix}`)
        .then(setData)
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [apiBase],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const openFolder = (item) => {
    if (!item.is_folder || !item.id) return;
    setFolderStack((stack) => [...stack, { id: item.id, name: item.name }]);
    void load(item.id);
  };

  const goUp = () => {
    if (!folderStack.length) return;
    const next = folderStack.slice(0, -1);
    setFolderStack(next);
    const parentId = next.length ? next[next.length - 1].id : null;
    void load(parentId);
  };

  if (loading && !data) return <p className="muted">Loading SharePoint files…</p>;
  if (error) return <p className="err">{error}</p>;

  if (!data?.configured) {
    return <p className="muted small">{data?.error || 'SharePoint is not configured for this folder.'}</p>;
  }

  return (
    <div>
      <div className="row-actions" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div className="muted small">
          {folderStack.length ? (
            <button type="button" className="btn-link" onClick={goUp}>
              ← Back
            </button>
          ) : (
            <span>Project / client folder</span>
          )}
          {folderStack.length ? (
            <span>
              {' '}
              / {folderStack.map((f) => f.name).join(' / ')}
            </span>
          ) : null}
        </div>
        {openInBrowserUrl || data.web_url ? (
          <a
            href={openInBrowserUrl || data.web_url}
            target="_blank"
            rel="noreferrer"
            className="btn-link small"
          >
            Open in SharePoint
          </a>
        ) : null}
      </div>
      {data.error && !data.items?.length ? <p className="err small">{data.error}</p> : null}
      {!data.items?.length ? <p className="muted">This folder is empty.</p> : null}
      <ul className="list-plain">
        {data.items?.map((item) => (
          <li key={item.id} className="auth-card flat" style={{ marginBottom: '0.5rem' }}>
            <div className="row-actions" style={{ justifyContent: 'space-between' }}>
              <div>
                <strong>{item.is_folder ? '📁 ' : '📄 '}{item.name}</strong>
                {!item.is_folder && item.size ? (
                  <span className="muted small"> · {formatSize(item.size)}</span>
                ) : null}
                {item.modified_at ? <div className="muted small">{item.modified_at}</div> : null}
              </div>
              <div className="row-actions">
                {item.is_folder ? (
                  <button type="button" className="btn-link small" onClick={() => openFolder(item)}>
                    Open
                  </button>
                ) : null}
                {item.web_url ? (
                  <a href={item.web_url} target="_blank" rel="noreferrer" className="btn-link small">
                    View
                  </a>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
